import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "use-intl";
import { SiteFooter } from "./SiteFooter";
import {
  createLoginReel,
  type ReelLabels,
  type ReelPhase,
  type ReelTerminalTone,
} from "../lib/login-reel";

const STEPS: { key: "rec" | "replay" | "insight" | "agent" | "verify"; phases: ReelPhase[] }[] = [
  { key: "rec", phases: ["form", "rec"] },
  { key: "replay", phases: ["rewind", "replay"] },
  { key: "insight", phases: ["insight"] },
  { key: "agent", phases: ["agent"] },
  { key: "verify", phases: ["verify", "dissolve"] },
];
// ターミナルの行: [その段の開始からのms, 文言のキー, 色]
const AGENT_LINES: [number, string, ReelTerminalTone][] = [
  [0, "a1", "prompt"],
  [700, "a2", "call"],
  [1150, "a3", "bad"],
  [1700, "a4", "call"],
  [2150, "a5", "dim"],
  [2700, "a6", "good"],
  [3100, "a7", "bad"],
  [3450, "a8", "good"],
  [4000, "a9", "call"],
];
const VERIFY_LINES: [number, string, ReelTerminalTone][] = [
  [0, "v1", "call"],
  [500, "v2", "good"],
  [1000, "v3", "good"],
];
const STREAM = [
  "click a.nav-pricing",
  "scroll 40%",
  "input ▇▇▇▇▇▇ masked",
  "rage click ×3 .price-toggle",
  "pageview /pricing",
  "dead click img.hero",
  "error TypeError: undefined",
  "conversion signup",
  "replay session 019b·7f2a",
];

/** ログイン画面の外枠。canvasをページ全面に敷き、左に「記録→再生」のリール、右にログインカード(children)を置く。 */
export const LoginShowreel = ({ children, header }: { children: ReactNode; header: ReactNode }) => {
  const t = useTranslations("auth.reel");
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<ReelPhase>("form");
  const stream = useMemo(() => [...STREAM, ...STREAM], []);
  const labels = useMemo<ReelLabels>(() => ({
    url: "shop.example/pricing",
    nav: [t("page.nav1"), t("page.nav2"), t("page.nav3")],
    cta: t("page.cta"),
    toggle: t("page.toggle"),
    plans: ["Free", "Pro", "Team"],
    clickPricing: t("events.clickPricing"),
    scroll: t("events.scroll"),
    inputMasked: t("events.inputMasked"),
    rage: t("events.rage"),
    conversion: t("events.conversion"),
    click: t("events.click"),
    recording: t("cursor.recording"),
    you: t("cursor.you"),
    visitor: t("cursor.visitor"),
    rewind: t("cursor.rewind"),
    replay: t("cursor.replay"),
    insightRage: t("insight.rage"),
    insightConversion: t("insight.conversion"),
    agentTitle: t("agent.title"),
    agentLines: AGENT_LINES.map(([at, key, tone]) => ({ at, text: t(`agent.${key}` as "agent.a1"), tone })),
    agentFixAt: 2700,
    verifyLines: VERIFY_LINES.map(([at, key, tone]) => ({ at, text: t(`agent.${key}` as "agent.v1"), tone })),
    verifyCallout: t("agent.callout"),
  }), [t]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    const frame = frameRef.current;
    const host = hostRef.current;
    if (!canvas || !overlay || !frame || !host) return;
    const reel = createLoginReel({ canvas, overlay, host, frame, labels, onPhase: setPhase });
    return () => reel.destroy();
  }, [labels]);

  const activeStep = STEPS.findIndex((step) => step.phases.includes(phase));

  return (
    <div className="login-page" ref={hostRef}>
      <canvas aria-hidden="true" className="login-reel-canvas" ref={canvasRef} />
      <canvas aria-hidden="true" className="login-reel-canvas" ref={overlayRef} />
      {header}
      <div className="login-stage">
        <section className="login-reel">
          <p className="login-reel-eyebrow">SESSION REPLAY FOR CODING AGENTS</p>
          <h2 className="login-reel-title">{t("headline")}</h2>
          <div className="login-reel-frame" ref={frameRef} />
          <ol className="login-reel-steps">
            {STEPS.map((step, index) => (
              <li className={index === activeStep ? "is-active" : index < activeStep ? "is-done" : undefined} key={step.key}>
                <b>{index + 1}</b>
                <span>{t(`steps.${step.key}`)}</span>
              </li>
            ))}
          </ol>
          <p className="login-reel-hint">{t("hint")}</p>
        </section>
        {children}
      </div>
      <div aria-hidden="true" className="login-reel-marquee">
        <div>
          {stream.map((item, index) => (
            <span key={`${item}-${index}`}>{item}</span>
          ))}
        </div>
      </div>
      <SiteFooter dark />
    </div>
  );
};
